import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Table, Thead, Tbody, Tr, Th, Td, Text, Checkbox, Flex, Button, Tfoot, Box } from '@chakra-ui/react'
import { DashboardProfile, SearchUsersResponse, UserStatus, UserSummary } from '../../services/network'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as eva from 'eva-icons'
import { createUsersApi } from '../../utils/api-interface'
import { useTranslation } from 'react-i18next'
import { DeleteConfirmationDialog } from '../DeleteConfirmationDialog/DeleteConfirmationDialog'
import { Link, useNavigate } from 'react-router-dom'
import { log } from '../../utils/logger'
import { RetryDialog } from '../RetryDialog/RetryDialog'
import axios from 'axios'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store/store'
import { setCurrentPage } from '../../store/slices/userSearchSlice'
import dayjs from 'dayjs'

interface IUserTableProps {
  query: string
  startDate: string
  endDate: string
  selectedStatuses: string[]
}

function UserTable({ startDate, endDate, selectedStatuses }: IUserTableProps): JSX.Element {
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([])
  const [selectAllChecked, setSelectAllChecked] = useState(false)
  const [searchResults, setSearchResults] = useState<SearchUsersResponse>()
  const { t } = useTranslation()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [itemToDelete, setItemToDelete] = useState<string | null>(null)
  const [errorData, setErrorData] = useState<any>(null)
  const [isRetryDialogOpen, setIsRetryDialogOpen] = useState(false)
  const navigate = useNavigate()

  const userSearch = useSelector((state: RootState) => state.searchUser)
  const dispatch = useDispatch()

  const handleCloseRetryDialog = (): void => {
    setIsRetryDialogOpen(false)
  }
  const dashboardUser: DashboardProfile | null = useSelector((state: RootState) => state.dashboardUser.dashboardUser)
  const canUpdate = useMemo(() => {
    if (!dashboardUser) {
      return false
    }

    if (!dashboardUser.role || !dashboardUser.role.title) {
      return false
    }

    return dashboardUser.role.title === process.env.REACT_APP_ROLE_ADMINISTRATOR || dashboardUser.role.title === process.env.REACT_APP_ROLE_BACKOFFICE
  }, [dashboardUser])

  const handleSearch = useCallback((): void => {
    const usersApi = createUsersApi()
    usersApi
      .searchUsers(
        userSearch.token ? userSearch.token.toLowerCase() : undefined,
        startDate ? dayjs(startDate).format(`YYYY-MM-DD`) : undefined,
        endDate ? dayjs(endDate).format(`YYYY-MM-DD`) : undefined,
        selectedStatuses.length > 0 ? selectedStatuses : undefined,
        userSearch.currentPage,
        10
      )
      .then((response) => {
        setSearchResults(response.data)
      })
      .catch((error) => {
        if (axios.isAxiosError(error)) {
          if (error.response) {
            log.error('Error fetching search results:', error.response.data)
            setErrorData(error.response.data)
          } else {
            log.error('No response from the server:', error.message)
          }
        } else {
          log.error('Unexpected error:', error)
        }
        setIsRetryDialogOpen(true)
      })
  }, [endDate, selectedStatuses, startDate, userSearch])

  const handleConfirmRetryDialog = (): void => {
    setIsRetryDialogOpen(false)
    handleSearch()
  }

  const handlePageChange = (page: number): void => {
    if (searchResults) {
      if (page > 0 && page <= searchResults?.totalPages) {
        dispatch(setCurrentPage(page))
      }
    }
  }

  const handleSelectAllChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e.target.checked) {
      if (searchResults) {
        setSelectedRowIds(searchResults.users.map((user) => user.id))
      }
    } else {
      setSelectedRowIds([])
    }
    setSelectAllChecked(e.target.checked)
  }

  const handleCheckboxChange = (userId: string): void => {
    setSelectedRowIds((prevSelectedRowIds) =>
      prevSelectedRowIds.includes(userId) ? prevSelectedRowIds.filter((id) => id !== userId) : [...prevSelectedRowIds, userId]
    )
  }

  const handleDeleteClick = useCallback(
    (userId?: string): void => {
      if (!canUpdate) {
        return
      }
      if (userId) {
        setItemToDelete(userId)
      }
      setIsDialogOpen(true)
    },
    [canUpdate]
  )

  const handleConfirmDelete = useCallback(async (): Promise<void> => {
    if (!canUpdate) {
      return
    }
    try {
      const usersApi = createUsersApi()
      if (itemToDelete) {
        const response = await usersApi.deleteUser(itemToDelete)
        if (response) {
          log.debug(response.data)
        }
      }
      if (selectedRowIds.length > 0) {
        const response = await usersApi.deleteUsers(selectedRowIds)
        if (response) {
          log.debug(response.data)
        }
      }
    } catch (error) {
      log.error('Error fetching search results:', error)
    }

    setIsDialogOpen(false)
    setItemToDelete(null)
  }, [canUpdate, itemToDelete, selectedRowIds])

  const handleCloseDialog = (): void => {
    setIsDialogOpen(false)
    setItemToDelete(null)
  }

  useEffect(() => {
    if (searchResults && searchResults.users) {
      if (selectedRowIds.length === searchResults?.users.length) {
        setSelectAllChecked(true)
      } else {
        setSelectAllChecked(false)
      }
    }
  }, [searchResults, selectedRowIds])

  useEffect(() => {
    if (!userSearch.token && !startDate && selectedStatuses.length === 0) {
      setSelectAllChecked(false)
      setSelectedRowIds([])
      setSearchResults(undefined)
    }
    const timeoutId = setTimeout(() => handleSearch(), 400)
    return () => clearTimeout(timeoutId)
  }, [userSearch.token, startDate, endDate, selectedStatuses, handleSearch])

  useEffect(() => eva.replace())

  const isUserDeleted = (user: UserSummary): boolean => {
    if (user.status === UserStatus.Deleted) {
      return true
    }
    if (user.status === UserStatus.Banned) {
      if (user.firstName === user.lastName) {
        if (user.lastName === 'DELETEDUSER') {
          return true
        }
      }
    }
    return false
  }

  const status = (user: UserSummary): string => {
    return isUserDeleted(user) ? `DeletedUser` : user.status
  }

  const goToUser = useCallback(
    (id: string) => {
      navigate(`/users/userinfo/${id}`)
    },
    [navigate]
  )

  return (
    <Flex flexDirection={'column'} width={'100%'}>
      <Text fontSize='26px' fontWeight='700' lineHeight='31.2px' textAlign='left' padding='26px'>
        {t('user_inscribed')}{' '}
      </Text>
      <Box overflowX='auto'>
        <Table bg={'white'}>
          <Thead height={'40px'}>
            {canUpdate && (
              <Th width='40px'>
                <Checkbox isChecked={selectAllChecked} onChange={handleSelectAllChange} />
              </Th>
            )}
            <Th width='121px'>{t('unique_code')}</Th>
            <Th width={'512px'}>{t('user_name')}</Th>
            <Th width={'120px'}>{t('birth_date')}</Th>
            <Th width={'90px'}>{t('status')}</Th>
            <Th width={'40px'}></Th>
            <Th width={'40px'}></Th>
          </Thead>
          <Tbody>
            {searchResults && searchResults.users && searchResults.users.length > 0 ? (
              searchResults.users.map((user, index) => (
                <Tr height={'40px !important'} key={index} className='row'>
                  {canUpdate && (
                    <Td>
                      <Checkbox isChecked={selectedRowIds.includes(user.id)} onChange={(): void => handleCheckboxChange(user.id)} />
                    </Td>
                  )}
                  <Td onClick={(): void => goToUser(user.id)}>{user.id}</Td>
                  <Td onClick={(): void => goToUser(user.id)}>
                    {user.firstName} {user.lastName}
                  </Td>
                  <Td onClick={(): void => goToUser(user.id)}>{dayjs(user.birthday).format(`DD/MM/YYYY`)}</Td>
                  <Td onClick={(): void => goToUser(user.id)}>
                    <Text variant={status(user)}>{t(status(user))}</Text>
                  </Td>
                  <Td>
                    {isUserDeleted(user) ? (
                      <></>
                    ) : (
                      canUpdate && (
                        <Button onClick={(): void => handleDeleteClick(user.id)}>
                          <i data-eva={'trash-2'} data-eva-fill={'#353F48'} data-eva-height='17' data-eva-width='16'></i>
                        </Button>
                      )
                    )}
                  </Td>
                  <Td>
                    <Link to={`/users/userinfo/${user.id}`}>
                      <Button>
                        <i data-eva={'edit-2'} data-eva-fill={'#353F48'} data-eva-height='18' data-eva-width='16'></i>
                      </Button>
                    </Link>
                  </Td>
                </Tr>
              ))
            ) : (
              <Tr>
                <Td colSpan={7} textAlign='center'>
                  {t('table_no_results')}
                </Td>
              </Tr>
            )}
          </Tbody>
          <Tfoot>
            <Tr>
              <Td colSpan={7} padding={'0px'}>
                <Flex direction='row' justifyContent='space-between' alignItems='center' bg={'greyWarm100'}>
                  {selectedRowIds.length > 0 ? (
                    <Flex direction={'row'} key='divSelectedElem'>
                      <Button onClick={(): void => handleDeleteClick()} bg={'#FBE4E2'} borderRadius={'0px'} key='buttonSelectedElem'>
                        <i data-eva={'trash-2'} data-eva-fill={'#CD1E58'}></i>
                      </Button>
                      <Text alignContent={'center'} fontSize='16px' fontWeight='600' lineHeight='22.4px' padding={'8px'}>
                        {selectedRowIds.length} {selectedRowIds.length === 1 ? t('selected_element') : t('selected_elements')}
                      </Text>
                    </Flex>
                  ) : (
                    <div></div>
                  )}
                  <Flex direction='row' marginLeft='auto' alignItems='center'>
                    <Button onClick={(): void => handlePageChange(userSearch.currentPage - 1)} isDisabled={userSearch.currentPage === 1}>
                      <i data-eva={'chevron-left-outline'} data-eva-fill={'#353F48'}></i>
                    </Button>
                    <Button
                      onClick={(): void => handlePageChange(userSearch.currentPage + 1)}
                      isDisabled={userSearch.currentPage === searchResults?.totalPages}>
                      <i data-eva={'chevron-right-outline'} data-eva-fill={'#353F48'}></i>
                    </Button>
                  </Flex>
                </Flex>
              </Td>
            </Tr>
          </Tfoot>
        </Table>
      </Box>

      <DeleteConfirmationDialog isOpen={isDialogOpen} onClose={handleCloseDialog} onConfirm={handleConfirmDelete} type='user' />
      <RetryDialog isOpen={isRetryDialogOpen} onClose={handleCloseRetryDialog} onConfirm={handleConfirmRetryDialog} message={errorData} />
    </Flex>
  )
}

export { UserTable }
